import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { Button, CircularProgress, IconButton, MenuItem, Select, Tooltip } from "@mui/material";
import Icon from "@mdi/react";
import {
	mdiCalendarClockOutline,
	mdiPause,
	mdiPlay,
	mdiRefresh,
	mdiTrashCanOutline,
} from "@mdi/js";
import { MainLayout } from "layouts";
import { useWorkspaceData } from "shared/hooks";
import {
	NotistackServices,
	PublicScheduledTaskDTO,
	PublicTaskExecutionLogDTO,
	ScheduledTaskServices,
	ScheduledTaskStatus,
} from "shared/services";
import { HandleError } from "shared/utils";
import styles from "./styles.module.scss";

const RECURRENCE_LABEL: Record<string, string> = {
	once: "Uma vez",
	daily: "Diária",
	weekly: "Semanal",
	monthly: "Mensal",
	cron: "Cron",
};

const STATUS_LABEL: Record<ScheduledTaskStatus, string> = {
	active: "Ativo",
	paused: "Pausado",
	archived: "Arquivado",
};

const RUN_STATUS_LABEL: Record<string, string> = {
	running: "Em execução",
	success: "Sucesso",
	failed: "Falha",
	partial: "Parcial",
};

const fmtDateTime = (iso: string | null | undefined): string => {
	if (!iso) return "—";
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return "—";
	return d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
};

const fmtDuration = (start: string, end: string | null): string => {
	if (!end) return "—";
	const ms = new Date(end).getTime() - new Date(start).getTime();
	if (!Number.isFinite(ms) || ms < 0) return "—";
	if (ms < 1000) return `${ms}ms`;
	if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
	return `${Math.floor(ms / 60_000)}m ${Math.floor((ms % 60_000) / 1000)}s`;
};

export const ScheduledTasksPage: React.FC = () => {
	const { workspace, loadingWorkspace } = useWorkspaceData();
	const workspaceId = workspace ? String(workspace.id) : null;

	const [scheduledTasks, setScheduledTasks] = useState<PublicScheduledTaskDTO[]>([]);
	const [loading, setLoading] = useState(false);
	const [statusFilter, setStatusFilter] = useState<"all" | ScheduledTaskStatus>("all");
	const [search, setSearch] = useState("");
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [logs, setLogs] = useState<PublicTaskExecutionLogDTO[]>([]);
	const [logsLoading, setLogsLoading] = useState(false);
	const fetchSeq = useRef(0);

	const reload = useCallback(async () => {
		if (!workspaceId) return;
		const seq = ++fetchSeq.current;
		setLoading(true);
		try {
			const data = await ScheduledTaskServices.list(workspaceId);
			if (fetchSeq.current === seq) {
				setScheduledTasks(data);
			}
		} catch (err) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(err) ? err.message : "Erro ao carregar agendamentos");
		} finally {
			if (fetchSeq.current === seq) setLoading(false);
		}
	}, [workspaceId]);

	useEffect(() => {
		void reload();
	}, [reload]);

	const filtered = useMemo(() => {
		let items = scheduledTasks;
		if (statusFilter !== "all") items = items.filter((t) => t.status === statusFilter);
		if (search.trim()) {
			const q = search.trim().toLowerCase();
			items = items.filter((t) => t.title.toLowerCase().includes(q));
		}
		return items;
	}, [scheduledTasks, statusFilter, search]);

	const selected = useMemo(
		() => scheduledTasks.find((t) => t.id === selectedId) ?? null,
		[scheduledTasks, selectedId],
	);

	useEffect(() => {
		if (!workspaceId || !selectedId) {
			setLogs([]);
			return;
		}
		let active = true;
		setLogsLoading(true);
		ScheduledTaskServices.listLogs(workspaceId, selectedId, 50)
			.then((data) => {
				if (active) setLogs(data);
			})
			.catch((err) => {
				if (active) {
					NotistackServices.showErrorMessage(HandleError.isHandleError(err) ? err.message : "Erro ao carregar logs");
				}
			})
			.finally(() => {
				if (active) setLogsLoading(false);
			});
		return () => {
			active = false;
		};
	}, [workspaceId, selectedId]);

	const handlePause = useCallback(
		async (id: string) => {
			if (!workspaceId) return;
			try {
				await ScheduledTaskServices.pause(workspaceId, id);
				NotistackServices.showSuccessMessage("Agendamento pausado");
				await reload();
			} catch (err) {
				NotistackServices.showErrorMessage(HandleError.isHandleError(err) ? err.message : "Erro ao pausar");
			}
		},
		[workspaceId, reload],
	);

	const handleResume = useCallback(
		async (id: string) => {
			if (!workspaceId) return;
			try {
				await ScheduledTaskServices.resume(workspaceId, id);
				NotistackServices.showSuccessMessage("Agendamento retomado");
				await reload();
			} catch (err) {
				NotistackServices.showErrorMessage(HandleError.isHandleError(err) ? err.message : "Erro ao retomar");
			}
		},
		[workspaceId, reload],
	);

	const handleCancel = useCallback(
		async (id: string) => {
			if (!workspaceId) return;
			if (!window.confirm("Cancelar este agendamento? Execuções futuras serão interrompidas.")) return;
			try {
				await ScheduledTaskServices.cancel(workspaceId, id);
				NotistackServices.showSuccessMessage("Agendamento cancelado");
				if (selectedId === id) setSelectedId(null);
				await reload();
			} catch (err) {
				NotistackServices.showErrorMessage(HandleError.isHandleError(err) ? err.message : "Erro ao cancelar");
			}
		},
		[workspaceId, reload, selectedId],
	);

	if (loadingWorkspace || !workspaceId) {
		return (
			<MainLayout>
				<div className={styles.loading}>
					<CircularProgress size={28} />
				</div>
			</MainLayout>
		);
	}

	return (
		<MainLayout>
			<div className={styles.page}>
				<div className={styles.header}>
					<div>
						<h1 className={styles.title}>Tarefas Agendadas</h1>
						<p className={styles.subtitle}>
							Visualize e gerencie tarefas que são executadas uma vez ou recorrentemente. Crie novas pelo chat dizendo "agendar X para
							toda sexta às 9h" ao assistente.
						</p>
					</div>
					<div className={styles.filters}>
						<input
							placeholder="Buscar por título..."
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							style={{
								padding: "8px 12px",
								borderRadius: 8,
								border: "1px solid var(--mui-palette-divider)",
								background: "var(--mui-palette-background-paper)",
								color: "var(--mui-palette-text-primary)",
								minWidth: 220,
							}}
						/>
						<Select
							size="small"
							value={statusFilter}
							onChange={(e) => setStatusFilter(e.target.value as any)}
						>
							<MenuItem value="all">Todos os status</MenuItem>
							<MenuItem value="active">Ativos</MenuItem>
							<MenuItem value="paused">Pausados</MenuItem>
							<MenuItem value="archived">Arquivados</MenuItem>
						</Select>
						<Tooltip title="Recarregar">
							<IconButton onClick={reload} disabled={loading}>
								{loading ? <CircularProgress size={18} /> : <Icon path={mdiRefresh} size={0.8} />}
							</IconButton>
						</Tooltip>
					</div>
				</div>

				{filtered.length === 0 ? (
					<div className={styles.empty}>
						<Icon path={mdiCalendarClockOutline} size={2} />
						<p>{loading ? "Carregando..." : "Nenhum agendamento encontrado."}</p>
					</div>
				) : (
					<div className={styles.tableWrapper}>
						<table className={styles.table}>
							<thead>
								<tr>
									<th>Título</th>
									<th>Recorrência</th>
									<th>Próxima execução</th>
									<th>Última execução</th>
									<th>Execuções</th>
									<th>Status</th>
									<th style={{ width: 140 }}>Ações</th>
								</tr>
							</thead>
							<tbody>
								{filtered.map((t) => (
									<tr
										key={t.id}
										className={clsx(selectedId === t.id && styles.rowSelected)}
										onClick={() => setSelectedId(t.id)}
									>
										<td data-label="Título">
											<div className={styles.titleCell}>{t.title}</div>
											{t.description && <div className={styles.subtle}>{t.description}</div>}
										</td>
										<td data-label="Recorrência">
											{RECURRENCE_LABEL[t.recurrenceType] ?? t.recurrenceType}
											{t.cronExpression && <div className={styles.subtle}>{t.cronExpression}</div>}
										</td>
										<td data-label="Próxima execução">{fmtDateTime(t.nextRunAt)}</td>
										<td data-label="Última execução">{fmtDateTime(t.lastRunAt)}</td>
										<td data-label="Execuções">
											{t.runsTotal} <span className={styles.subtle}>({t.runsSucceeded} ok / {t.runsFailed} falha)</span>
										</td>
										<td data-label="Status">
											<span className={clsx(styles.statusChip, styles[`status_${t.status}` as const])}>
												{STATUS_LABEL[t.status]}
											</span>
										</td>
										<td data-label="Ações" onClick={(e) => e.stopPropagation()}>
											{t.status === "active" && (
												<Tooltip title="Pausar">
													<IconButton size="small" onClick={() => handlePause(t.id)}>
														<Icon path={mdiPause} size={0.7} />
													</IconButton>
												</Tooltip>
											)}
											{t.status === "paused" && (
												<Tooltip title="Retomar">
													<IconButton size="small" onClick={() => handleResume(t.id)}>
														<Icon path={mdiPlay} size={0.7} />
													</IconButton>
												</Tooltip>
											)}
											{t.status !== "archived" && (
												<Tooltip title="Cancelar">
													<IconButton size="small" onClick={() => handleCancel(t.id)}>
														<Icon path={mdiTrashCanOutline} size={0.7} />
													</IconButton>
												</Tooltip>
											)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}

				{selected && (
					<div className={styles.detail}>
						<div className={styles.detailHeader}>
							<div>
								<h2 className={styles.detailTitle}>{selected.title}</h2>
								<div className={styles.subtle}>{selected.cronExpression ?? RECURRENCE_LABEL[selected.recurrenceType]}</div>
							</div>
							<Button size="small" onClick={() => setSelectedId(null)}>Fechar</Button>
						</div>

						<div className={styles.detailGrid}>
							<div className={styles.detailItem}>
								<span className={styles.detailLabel}>Status</span>
								<span className={styles.detailValue}>{STATUS_LABEL[selected.status]}</span>
							</div>
							<div className={styles.detailItem}>
								<span className={styles.detailLabel}>Timezone</span>
								<span className={styles.detailValue}>{selected.timezone}</span>
							</div>
							<div className={styles.detailItem}>
								<span className={styles.detailLabel}>Próxima execução</span>
								<span className={styles.detailValue}>{fmtDateTime(selected.nextRunAt)}</span>
							</div>
							<div className={styles.detailItem}>
								<span className={styles.detailLabel}>Última execução</span>
								<span className={styles.detailValue}>{fmtDateTime(selected.lastRunAt)}</span>
							</div>
							<div className={styles.detailItem}>
								<span className={styles.detailLabel}>Total de execuções</span>
								<span className={styles.detailValue}>{selected.runsTotal}</span>
							</div>
							<div className={styles.detailItem}>
								<span className={styles.detailLabel}>Sucesso / Falha</span>
								<span className={styles.detailValue}>
									{selected.runsSucceeded} / {selected.runsFailed}
								</span>
							</div>
						</div>

						<div>
							<h3 style={{ margin: "0 0 8px", fontSize: "0.95rem" }}>Histórico de execuções</h3>
							{logsLoading ? (
								<div className={styles.loading}>
									<CircularProgress size={20} />
								</div>
							) : logs.length === 0 ? (
								<div className={styles.subtle}>Sem execuções registradas ainda.</div>
							) : (
								<table className={styles.logsTable}>
									<thead>
										<tr>
											<th>#</th>
											<th>Início</th>
											<th>Duração</th>
											<th>Status</th>
											<th>Detalhe</th>
										</tr>
									</thead>
									<tbody>
										{logs.map((log) => (
											<tr key={log.id}>
												<td>{log.runNumber}</td>
												<td>{fmtDateTime(log.startedAt)}</td>
												<td>{fmtDuration(log.startedAt, log.finishedAt)}</td>
												<td>
													<span className={clsx(styles.statusChip, styles[`run_${log.status}` as const])}>
														{RUN_STATUS_LABEL[log.status] ?? log.status}
													</span>
												</td>
												<td className={styles.subtle}>
													{log.failureReason ?? (log.taskId ? `Task: ${log.taskId.slice(0, 8)}…` : "—")}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							)}
						</div>
					</div>
				)}
			</div>
		</MainLayout>
	);
};

export default ScheduledTasksPage;
